import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { api } from './api';
import { copyText } from './clipboard';

// Second-factor step shown after a password-only login for admin / owner-admin
// accounts. Two shapes depending on `enrolled`:
//   - not enrolled: fetch a fresh TOTP secret, show it for manual entry into an
//     authenticator app, confirm with a 6-digit code, then show one-time
//     recovery codes.
//   - enrolled: single code input (TOTP or recovery code) that verifies the
//     existing challenge.
export default function MfaStep({ mfaToken, enrolled, onDone, onCancel }) {
  const [enrollData, setEnrollData] = useState(null); // {secret, otpauth_uri, issuer, account}
  const [recoveryCodes, setRecoveryCodes] = useState(null); // set after successful enroll confirm
  const [pendingDone, setPendingDone] = useState(null); // access_token/user payload waiting on the "I saved these" ack
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [starting, setStarting] = useState(!enrolled);

  useEffect(() => {
    if (enrolled) return;
    let cancelled = false;
    setStarting(true);
    api.post('/auth/mfa/enroll/start', { token: mfaToken })
      .then(({ data }) => { if (!cancelled) setEnrollData(data); })
      .catch((e) => { if (!cancelled) setError(describeError(e)); })
      .finally(() => { if (!cancelled) setStarting(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolled, mfaToken]);

  const describeError = (e) => {
    const status = e?.response?.status;
    if (status === 401) return 'Invalid code';
    if (status === 429) return 'Too many attempts — wait a few minutes and try again';
    // Ticket 303: enroll start/confirm answer 409 already_enrolled when another tab finished first.
    if (status === 409 && !enrolled && e?.response?.data?.detail === 'already_enrolled') {
      return 'Two-factor was just set up from another tab or window. Sign in again to continue.';
    }
    if (status === 409) return "Two-factor isn't set up for this account";
    return e?.response?.data?.detail || 'Something went wrong';
  };

  const copySecret = async () => {
    if (!enrollData?.secret) return;
    const ok = await copyText(enrollData.secret);
    setCopied(ok ? 'copied' : 'failed');
    setTimeout(() => setCopied(false), 2000);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (enrolled) {
        const { data } = await api.post('/auth/mfa/verify', { token: mfaToken, code });
        onDone(data);
      } else {
        const { data } = await api.post('/auth/mfa/enroll/confirm', { token: mfaToken, code });
        setRecoveryCodes(data.recovery_codes);
        setPendingDone({ access_token: data.access_token, user: data.user });
      }
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  // Recovery-codes screen shown once, after a successful first-time enroll.
  if (recoveryCodes) {
    return (
      <div className="space-y-4">
        <h2 className="font-serif text-2xl font-semibold" style={{ color: '#070B5F' }}>
          Save your recovery codes
        </h2>
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          These codes are shown only once. Store them somewhere safe — each one can be used
          instead of your authenticator app if you lose access to it.
        </p>
        <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-50 border border-slate-200 rounded-lg p-4">
          {recoveryCodes.map((c) => (
            <div key={c}>{c}</div>
          ))}
        </div>
        <Button
          type="button"
          className="w-full justify-center"
          onClick={() => onDone(pendingDone)}
          data-testid="mfa-recovery-ack"
        >
          I saved these codes — continue
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="font-serif text-2xl font-semibold" style={{ color: '#070B5F' }}>
        {enrolled ? 'Enter your authentication code' : 'Set up two-factor authentication'}
      </h2>

      {!enrolled && (
        <>
          {starting && <p className="text-sm text-slate-500">Preparing your authenticator setup…</p>}
          {enrollData && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Scan this QR code with your authenticator app (Google Authenticator, 1Password,
                Authy…), then enter the 6-digit code it generates below.
              </p>
              <div
                className="flex justify-center bg-white border border-slate-200 rounded-lg p-4"
                data-testid="mfa-qr"
              >
                <QRCodeSVG value={enrollData.otpauth_uri} size={192} marginSize={0} />
              </div>
              {!showManualEntry ? (
                <button
                  type="button"
                  onClick={() => setShowManualEntry(true)}
                  className="text-sm font-semibold hover:underline"
                  style={{ color: '#6BA82E' }}
                  data-testid="mfa-manual-toggle"
                >
                  Can't scan? Enter it manually
                </button>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm" style={{ color: '#070B5F' }}>Secret key</Label>
                    <div className="mt-1.5 flex items-center gap-2">
                      <code
                        className="flex-1 font-mono text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 break-all"
                        data-testid="mfa-secret"
                      >
                        {enrollData.secret}
                      </code>
                      <Button type="button" variant="outline" size="sm" onClick={copySecret}>
                        {copied === 'copied' ? 'Copied' : copied === 'failed' ? 'Copy failed' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                  <a
                    href={enrollData.otpauth_uri}
                    className="text-sm font-semibold hover:underline"
                    style={{ color: '#6BA82E' }}
                  >
                    Open in authenticator app
                  </a>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-sm" htmlFor="mfa-code" style={{ color: '#070B5F' }}>
            {enrolled ? 'Authentication code' : 'Confirmation code'}
          </Label>
          <Input
            id="mfa-code"
            className="mt-1.5"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={enrolled ? '123456 or recovery code' : '123456'}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            data-testid="mfa-code-input"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert" data-testid="mfa-error">{error}</p>
        )}

        <Button type="submit" disabled={busy || (!enrolled && !enrollData)} className="w-full justify-center" data-testid="mfa-submit">
          {busy ? 'Please wait...' : 'Submit'}
        </Button>
      </form>

      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-slate-500 hover:underline"
          data-testid="mfa-cancel"
        >
          Cancel
        </button>
      )}
    </div>
  );
}
